import React, { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { blogAPI } from './services/api';

import PrivateRoute from './components/PrivateRoute';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import VantaBackground from './components/VantaBackground';
import PostSummary from './components/PostSummary';
import Pagination from './components/Pagination';
import CloudLoading from './components/CloudLoading';
import CloudArtwork from './components/CloudArtwork';
import usePageReady from './hooks/usePageReady';
import useSkinChangeFeedback from './hooks/useSkinChangeFeedback';
import SiteHeader from './components/SiteHeader';
import ThemeIcon from './components/ThemeIcon';
import LetterPostmark from './components/LetterPostmark';
import AnimatedDisclosure from './components/AnimatedDisclosure';
import NotFound from './components/NotFound';
import { getErrorKind } from './utils/requestErrors';
import { getBeijingPeriod } from './utils/skyTheme';
import { getSkinColor, isSkin, readSavedSkin, SKIN_STORAGE_KEY } from './utils/skins';
import { SkinContext, SkinSettingsContext, useSkinSettings } from './components/SkinContext';
import './App.css';
import './CloudTheme.css';
import './GlassControls.css';
import './Scrollbars.css';
import './TimeOfDayTheme.css';
import './skins/AdditionalSkins.css';
import './skins/OrbitalSkin.css';
import './skins/TerminalSkin.css';
import './skins/AtelierSkin.css';
import './skins/ComponentSkins.css';
import './skins/AdminSkins.css';
import './skins/ExperienceLayouts.css';
import './skins/ReadingLayouts.css';
import './skins/DisclosurePerformance.css';
import './skins/SkinTypography.css';
import './skins/ThemeRedesign.css';
import './components/IconActions.css';
import './skins/AtelierLetters.css';
import './components/ControlsRefinement.css';
import './skins/ReadingRefinement.css';
import './skins/TerminalRefinement.css';
import './skins/ArchiveTree.css';
import './skins/AboutRefinement.css';

const AdminLogin = lazy(() => import('./pages/Admin/Login'));
const AdminDashboard = lazy(() => import('./pages/Admin/Dashboard'));
const AdminComments = lazy(() => import('./pages/Admin/AdminComments'));
const CreatePost = lazy(() => import('./pages/Admin/CreatePost'));
const EditPost = lazy(() => import('./pages/Admin/EditPost'));
const SystemMonitor = lazy(() => import('./pages/Admin/SystemMonitor'));
const AdminLayout = lazy(() => import('./pages/Admin/AdminLayout'));
const loadPostPage = () => import('./pages/Post');
const Post = lazy(loadPostPage);
const Guestbook = lazy(() => import('./pages/Guestbook/Guestbook'));

const warmPostDestination = (postId) => {
  loadPostPage().catch(() => {});
};

const PostLink = ({ postId, children, ...props }) => (
  <Link
    to={`/post/${postId}`}
    onMouseEnter={() => warmPostDestination(postId)}
    onFocus={() => warmPostDestination(postId)}
    onTouchStart={() => warmPostDestination(postId)}
    {...props}
  >
    {children}
  </Link>
);

// 工具函数：计算文章显示ID（从1开始，置顶优先，然后按发布时间降序）
const calculateDisplayIds = (posts) => {
  if (!posts || posts.length === 0) return [];
  
  // 按置顶状态降序排序，然后按创建时间降序排序（置顶的在前，同状态下最新的在前）
  const sortedPosts = [...posts].sort((a, b) => {
    // 先按置顶状态排序（置顶的在前）
    if (a.is_top !== b.is_top) {
      return b.is_top ? 1 : -1;
    }
    // 同样置顶状态下，按创建时间排序（最新的在前）
    const dateA = new Date(a.created_at || a.date);
    const dateB = new Date(b.created_at || b.date);
    return dateB - dateA;
  });
  
  // 为每篇文章分配显示ID
  return sortedPosts.map((post, index) => ({
    ...post,
    displayId: index + 1
  }));
};

function App() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const needsPosts = ['/', '/blog', '/archives'].includes(location.pathname);
  const [skin, setSkin] = useState(readSavedSkin);
  const prepareSkinChange = useSkinChangeFeedback(skin);
  const [period, setPeriod] = useState(getBeijingPeriod);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contentReady, setContentReady] = useState(false);
  const [postsError, setPostsError] = useState(null);
  const [postsAttempt, setPostsAttempt] = useState(0);
  const lastPostsLoadRef = useRef(0);

  useEffect(() => {
    const synchronizeSkin = event => {
      if (event.key === SKIN_STORAGE_KEY) { setSkin(readSavedSkin()); setPeriod(getBeijingPeriod()); }
    };
    window.addEventListener('storage', synchronizeSkin);
    return () => window.removeEventListener('storage', synchronizeSkin);
  }, []);

  useEffect(() => {
    const updatePeriod = () => setPeriod(getBeijingPeriod());
    let timer;
    const scheduleNextMinute = () => {
      timer = window.setTimeout(() => {
        updatePeriod();
        scheduleNextMinute();
      }, 60 * 1000 - Date.now() % (60 * 1000) + 20);
    };
    scheduleNextMinute();
    window.addEventListener('focus', updatePeriod);
    document.addEventListener('visibilitychange', updatePeriod);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('focus', updatePeriod);
      document.removeEventListener('visibilitychange', updatePeriod);
    };
  }, []);

  const selectSkin = nextSkin => {
    if (!isSkin(nextSkin)) return;
    prepareSkinChange(nextSkin);
    try { window.localStorage.setItem(SKIN_STORAGE_KEY, nextSkin); } catch {}
    setPeriod(getBeijingPeriod());
    setSkin(nextSkin);
  };

  const publicTheme = { 'data-skin': skin, 'data-period': period };

  useEffect(() => {
    const themeColor = getSkinColor(skin, period);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', themeColor);
    // Public pages, the workspace, and body portals share one saved preference.
    document.body.dataset.publicSkin = skin;
    document.body.dataset.skinPeriod = period;
    return () => { delete document.body.dataset.publicSkin; delete document.body.dataset.skinPeriod; };
  }, [isAdminRoute, skin, period]);



// 加载文章
  useEffect(() => {
    if (!needsPosts) return undefined;
    let active = true;
    const loadPosts = async () => {
      setLoading(true);
      setPostsError(null);
      try {
        const data = await blogAPI.getPosts({ compact: true });
        if (!active) return;
        setPosts(data);
        lastPostsLoadRef.current = Date.now();
      } catch (error) {
        if (!active) return;
        console.error('加载文章失败:', error);
        setPostsError(getErrorKind(error));
      } finally {
        if (active) {
          setLoading(false);
          setContentReady(true);
        }
      }
    };

    loadPosts();
    return () => { active = false; };
  }, [postsAttempt, needsPosts]);

  useEffect(() => {
    const onFocus = async () => {
      if (!needsPosts) return;
      if (Date.now() - lastPostsLoadRef.current < 5 * 60 * 1000) return;
      try {
        const data = await blogAPI.getPosts({ compact: true });
        setPosts(data);
        setPostsError(null);
        lastPostsLoadRef.current = Date.now();
      } catch {}
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [needsPosts]);

// 如果还在加载中，显示加载页面
  if (needsPosts && (loading || !contentReady)) {
    return (
      <SkinSettingsContext.Provider value={{ skin, period, onSkinChange: selectSkin }}><SkinContext.Provider value={skin}><div className="app public-app" {...publicTheme}>
        <VantaBackground skin={skin} period={period} />
        <CloudLoading label="正在准备这一页…" />
      </div></SkinContext.Provider></SkinSettingsContext.Provider>
    );
  }

  return (
    <SkinSettingsContext.Provider value={{ skin, period, onSkinChange: selectSkin }}><SkinContext.Provider value={skin}><div className={`app ${isAdminRoute ? 'admin-app' : 'public-app'}`} {...publicTheme}>
      <VantaBackground skin={skin} period={period} />
      <ScrollToTop />
      {!isAdminRoute && <SiteHeader skin={skin} period={period} onSkinChange={selectSkin} />}
        
        <main id="main-content" className="main-content" style={isAdminRoute ? { padding: 0 } : {}}>
          <ErrorBoundary key={location.pathname}>
            <Suspense fallback={<CloudLoading label="正在展开这一页…" />}>
              {needsPosts && postsError ? <NotFound kind={postsError} onRetry={() => setPostsAttempt(value => value + 1)} /> : <Routes>
              <Route path="/" element={<Home posts={posts} loading={loading} />} />
              <Route path="/blog" element={<Blog posts={posts} loading={loading} />} />
              <Route path="/guestbook" element={<Guestbook />} />
              <Route path="/archives" element={<Archives posts={posts} loading={loading} />} />
              <Route path="/post/:id" element={<Post />} />
              <Route path="/about" element={<About />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/404" element={<NotFound />} />
              
              <Route path="/admin" element={
                <PrivateRoute>
                  <AdminLayout />
                </PrivateRoute>
              }>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="comments" element={<AdminComments />} />
                <Route path="create-post" element={<CreatePost />} />
                <Route path="edit-post/:id" element={<EditPost />} />
                <Route path="system-monitor" element={<SystemMonitor />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              
              <Route path="*" element={<NotFound />} />
              </Routes>}
            </Suspense>
          </ErrorBoundary>
        </main>
        
        {!isAdminRoute && (
        <footer className="footer">
            <div className="footer-note"><span><ThemeIcon name="cloud" /></span> 在这里，记录每一次灵光乍现。</div>
            <p>
              © {new Date().getFullYear()} 双霸服的霸服窝
              {process.env.REACT_APP_ICP_CODE ? (
                <> | 备案号：<a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">{process.env.REACT_APP_ICP_CODE}</a></>
              ) : null}
            </p>
            <p className="font-credit">字体鸣谢 <a href="https://hyperos.mi.com/font/zh/" target="_blank" rel="noopener noreferrer">MiSans</a> · Noto Sans SC</p>
          </footer>
        )}
      </div></SkinContext.Provider></SkinSettingsContext.Provider>
  );
}

// 首页组件
function Home({ posts, loading }) {
  const { skin, period } = useSkinSettings();
  const homeCopy = {
    regular: ['THOUGHTS, CODE & A LITTLE WONDER', '想法漫游，', '在此停留。', '最近的记录'],
    dynamic: [({ day: 'DAYLIGHT NOTES', dusk: 'EVENING NOTES', night: 'NIGHT NOTES' })[period], '此刻，', '读一点新鲜。', '沿途的记录'],
    orbital: ['A MAP OF IDEAS', '保持好奇，', '向想法出发。', '继续探索'],
    terminal: ['~/journal', '打开记录，', '发现下一行。', '最近的文件'],
    atelier: ['THE PERSONAL JOURNAL', '纸短，', '故事还长。', '本期选读'],
  }[skin];
  usePageReady(!loading);
  // 过滤掉隐藏的文章并计算显示ID
  const visiblePosts = posts.filter(post => !post.is_hidden);
  const postsWithDisplayIds = calculateDisplayIds(visiblePosts);
  const tagCount = new Set(visiblePosts.flatMap(post => post.tags || [])).size;
  const latestPostDate = postsWithDisplayIds[0]?.created_at || postsWithDisplayIds[0]?.date;
  
  if (loading) {
    return (
      <div className="container page-loading">
        <CloudLoading />
      </div>
    );
  }

  const isLetterTheme = skin === 'atelier';
  const featuredPost = postsWithDisplayIds[0];
  const recentPosts = isLetterTheme ? postsWithDisplayIds.slice(1, 4) : postsWithDisplayIds.slice(0, 3);
  const featuredCard = (
    <div className={`cloud-sheet${featuredPost ? ' article-card' : ''}${isLetterTheme ? ' atelier-letter' : ''}`}>
      {isLetterTheme && <LetterPostmark />}
      <div className="cloud-sheet-label"><span className="cloud-dot" /> {featuredPost?.is_top ? '置顶手记' : '最近在写'}<span>THE JOURNAL</span>
        {isLetterTheme && featuredPost && <time className="feature-date post-date" dateTime={featuredPost.created_at || undefined}>{featuredPost.created_at ? new Date(featuredPost.created_at).toLocaleDateString() : featuredPost.date}</time>}
      </div>
      {featuredPost ? <>
        <span className="feature-topic">{featuredPost.tags?.[0] || '开发与日常'}</span>
        <h3><PostLink postId={featuredPost.id} className="article-card-link" title={featuredPost.ai_summary || undefined}>{featuredPost.title}</PostLink></h3>
        <PostSummary aiSummary={featuredPost.ai_summary} maxLength={88} />
      </> : <><h3>好奇心，是一切的开始。</h3><p>新的故事正在酝酿，欢迎常来坐坐。</p></>}
    </div>
  );
  
  return (
    <div className="container home-container page-animate">
      <div className="hero slide-up-animate">
        {isLetterTheme && <CloudArtwork className="hero-cloudscape atelier-hero-artwork" variant="compact" />}
        <div className="hero-copy">
          <div className="hero-kicker"><span className="tiny-orbit" /> {homeCopy[0]}</div>
          <h2>{homeCopy[1]}<br /><span>{homeCopy[2]}</span></h2>
          <p className="subtitle">把代码里的探索，生活里的灵感，<br className="desktop-break" />慢慢写成可以与你分享的故事。</p>
          <div className="hero-actions">
            <Link to="/blog" className="button hero-primary icon-action" aria-label="开始阅读" title="开始阅读" data-tooltip="开始阅读"><ThemeIcon name="book" variant="action" /></Link>
            <Link to="/archives" className="hero-secondary icon-action" aria-label="按时间浏览" title="按时间浏览" data-tooltip="按时间浏览"><ThemeIcon name="archive" variant="action" /></Link>
          </div>
          <dl className="hero-stats" aria-label="博客数据概览">
            <div><dt>{visiblePosts.length}</dt><dd>篇公开文章</dd></div>
            <div><dt>{tagCount}</dt><dd>个技术主题</dd></div>
            <div><dt>{latestPostDate ? new Date(latestPostDate).getFullYear() : '—'}</dt><dd>持续更新</dd></div>
          </dl>
        </div>
        {!isLetterTheme && <div className="hero-sky">
          <CloudArtwork className="hero-cloudscape" variant="compact" />
          {featuredCard}
          <div className="sky-caption"><span aria-hidden="true"><ThemeIcon name="cloud" /></span> 保持好奇，慢慢生长。</div>
        </div>}
      </div>
      
      <div className="recent-posts">
        <div className="section-heading slide-up-animate">
          <div>
            <span className="section-eyebrow">THE LATEST CHAPTERS</span>
            <h3>{homeCopy[3]}<span className="heading-dot">.</span></h3>
          </div>
          <Link to="/blog" className="section-link icon-action" aria-label="查看全部文章" title="查看全部文章" data-tooltip="全部文章"><ThemeIcon name="collection" variant="action" /></Link>
        </div>
        
        {postsWithDisplayIds.length > 0 ? (
          <>
            <div className="posts-grid stagger-animate">
              {isLetterTheme && featuredCard}
              {recentPosts.map(post => (
                <div className={`post-card article-card${skin === 'atelier' ? ' atelier-letter' : ''}`} key={post.id}>
                  {skin === 'atelier' && <LetterPostmark />}
                  <div className="post-card-topline">
                    <span className="post-card-index">{String(post.displayId).padStart(2, '0')}</span>
                    <div className="post-date">{post.created_at ? new Date(post.created_at).toLocaleDateString() : post.date}</div>
                  </div>
                  <h4><PostLink postId={post.id} className="article-card-link" title={post.ai_summary || undefined}>{post.title}</PostLink>{post.is_top && <span className="post-top-badge">置顶</span>}</h4>
                  <PostSummary 
                    aiSummary={post.ai_summary} 
                    maxLength={80}
                  />
                </div>
              ))}
            </div>
            
            <div className="view-all slide-up-animate">
              <Link to="/blog" className="button icon-action" aria-label="查看全部文章" title="查看全部文章" data-tooltip="全部文章"><ThemeIcon name="collection" variant="action" /></Link>
            </div>
          </>
        ) : (
          <div className="no-posts slide-up-animate">
            <p>暂无文章，请稍后再试</p>
          </div>
        )}
      </div>
    </div>
  );
}

// 博客列表页组件
function Blog({ posts, loading }) {
  const { skin, period } = useSkinSettings();
  const [searchPosts, setSearchPosts] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [searchAttempt, setSearchAttempt] = useState(0);
  usePageReady(!loading && !searchLoading);
  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 5;

  // Full text is fetched only when a reader actually searches. Cards use the
  // compact list, while searching still covers the entire article body.
  useEffect(() => {
    if (!submittedQuery || searchPosts) return undefined;
    let active = true;
    setSearchLoading(true);
    setSearchError(false);
    blogAPI.getPosts().then(data => { if (active) setSearchPosts(data); })
      .catch(() => { if (active) setSearchError(true); })
      .finally(() => { if (active) setSearchLoading(false); });
    return () => { active = false; };
  }, [submittedQuery, searchPosts, searchAttempt]);

  const performSearch = () => {
    setSubmittedQuery(query.trim());
    setCurrentPage(1); // 搜索时重置到第一页
  };
  const clearSearch = () => {
    setQuery('');
    setSubmittedQuery('');
    setCurrentPage(1);
  };

  // 计算显示ID
  const visiblePosts = posts.filter(post => !post.is_hidden);
  const postsWithDisplayIds = calculateDisplayIds((submittedQuery && searchPosts ? searchPosts : visiblePosts).filter(post => !post.is_hidden));

  const filtered = submittedQuery
    ? postsWithDisplayIds.filter(p => {
        const q = submittedQuery.toLowerCase();
        const text = [
          p.title || '',
          p.excerpt || '',
          p.content || ''
        ].join(' ').toLowerCase();
        return text.includes(q);
      })
    : postsWithDisplayIds;

  // 分页计算
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pagedPosts = filtered.slice(startIndex, startIndex + PAGE_SIZE);

  // 处理页码切换
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="container">
        <CloudLoading />
      </div>
    );
  }

  return (
    <div className="container blog-container page-animate">
      <div className="blog-header slide-up-animate">
        <div><span className="section-eyebrow">THE JOURNAL</span><h2 className="page-title">所有文章<span className="heading-dot">.</span></h2><p className="page-description">技术的来路，想法的去处。共 {visiblePosts.length} 篇记录。</p></div>
        <div className="blog-search-bar">
          <div className="search-input-wrapper">
            <input
              type="text"
              className="blog-search"
              placeholder="搜索标题/内容..."
              aria-label="搜索文章标题或内容"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') performSearch();
              }}
            />
            {query && (
              <button
                type="button"
                className="blog-clear-icon"
                onClick={clearSearch}
                title="清除搜索"
                aria-label="清除搜索"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
          <button type="button" className="blog-search-btn" onClick={performSearch} aria-label="搜索">
            {skin !== 'regular' ? <ThemeIcon name="search" skin={skin} period={period} /> : <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>}
          </button>
        </div>
        {skin !== 'regular' && <nav className="journal-shortcuts" aria-label="更多阅读方式">
          <Link to="/archives" className="icon-action" aria-label="按时间浏览" title="按时间浏览" data-tooltip="按时间浏览"><ThemeIcon name="archive" variant="action" skin={skin} period={period} /></Link>
          <Link to="/guestbook" className="icon-action" aria-label="打开留言板" title="打开留言板" data-tooltip="留言板"><ThemeIcon name="message" variant="action" skin={skin} period={period} /></Link>
        </nav>}
      </div>

      <div className="blog-results">
      {submittedQuery && (searchLoading || (!searchPosts && !searchError)) ? <CloudLoading label="正在搜索全文…" /> : submittedQuery && searchError ? (
        <div className="empty-state" role="alert"><p>全文搜索暂时未能加载，请重试。</p><button className="button" onClick={() => setSearchAttempt(value => value + 1)}>重新搜索</button></div>
      ) : filtered.length > 0 ? (
        <>
          <div className="posts-list stagger-animate" key={`${currentPage}-${submittedQuery}`}>
            {skin === 'terminal' && <div className="terminal-journal-toolbar">
              <span className="terminal-window-dots" aria-hidden="true"><i /><i /><i /></span>
              <span className="terminal-journal-title"><ThemeIcon name="journal" /> journal</span>
              <span className="terminal-journal-count">{filtered.length} 篇文章</span>
            </div>}
            {pagedPosts.map(post => (
              <div className={`blog-post article-card${skin === 'atelier' ? ' atelier-letter' : ''}`} key={post.id}>
                {skin === 'atelier' && <LetterPostmark />}
                <div className="post-header">
                  <h3><PostLink postId={post.id} className="article-card-link" title={post.ai_summary || undefined}>{post.title}</PostLink>{post.is_top && <span className="post-top-badge">置顶</span>}</h3>
                  <span className="post-date">
                    {post.created_at ? new Date(post.created_at).toLocaleDateString() : post.date}
                  </span>
                </div>
                <PostSummary
                  aiSummary={post.ai_summary}
                  maxLength={120}
                />
              </div>
            ))}
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            scrollTargetSelector=".posts-list"
            scrollOffset={100}
          />
        </>
      ) : (
        <div className="empty-state slide-up-animate">
          <div className="empty-title">{submittedQuery ? '没有找到匹配的文章' : '暂无文章'}</div>
          <div className="empty-desc">{submittedQuery ? '试试更短的关键词，或清除搜索查看所有文章。' : '暂时没有文章，稍后再来看看吧'}</div>
          <div className="empty-actions">
            <Link to="/" className="button icon-action" aria-label="返回首页" title="返回首页" data-tooltip="返回首页"><ThemeIcon name="home" variant="action" /></Link>
            <Link to="/guestbook" className="button button-secondary">去留言板</Link>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function Archives({ posts, loading }) {
  usePageReady(!loading);
  const [collapsedYears, setCollapsedYears] = React.useState(new Set());
  
  // 计算显示ID
  const visiblePosts = posts.filter(post => !post.is_hidden);
  const postsWithDisplayIds = calculateDisplayIds(visiblePosts);
  
  const yearGroups = React.useMemo(() => {
    const map = new Map();
    postsWithDisplayIds.forEach(p => {
      const d = p.created_at ? new Date(p.created_at) : (p.date ? new Date(p.date) : null);
      if (!d || Number.isNaN(d?.getTime())) return;
      const y = d.getFullYear();
      const m = String(d.getMonth()+1).padStart(2,'0');
      const ym = `${y}-${m}`;
      const item = { ...p, _date: d };
      if (!map.has(y)) map.set(y, new Map());
      const mm = map.get(y);
      if (!mm.has(ym)) mm.set(ym, []);
      mm.get(ym).push(item);
    });
    return Array.from(map.entries())
      .sort((a,b)=>b[0]-a[0])
      .map(([year, mm]) => ({
        year,
        months: Array.from(mm.entries())
          .sort((a,b)=>b[0].localeCompare(a[0]))
          .map(([key, list]) => ({ key, list: list.sort((a,b)=> {
            // 仅按时间排序
            return b._date - a._date;
          }) }))
      }));
  }, [postsWithDisplayIds]);
  const toggleYear = (y) => {
    setCollapsedYears(prev => {
      const next = new Set(prev);
      if (next.has(y)) next.delete(y); else next.add(y);
      return next;
    });
  };
  if (loading) return <CloudLoading label="正在整理时间里的足迹…" />;
  return (
    <div className="container archives-container archive-tree page-animate">
      <div className="page-intro archive-intro"><div><span className="section-eyebrow">ALONG THE WAY</span><h2 className="page-title slide-up-animate">时间里的足迹<span className="heading-dot">.</span></h2><p className="page-description">翻一翻过去，也许会遇见新的灵感。</p></div><div className="archive-total"><strong>{visiblePosts.length}</strong><span>篇记录，慢慢积累</span></div></div>
      {yearGroups.length === 0 ? (
        <div className="no-posts slide-up-animate">暂无文章</div>
      ) : (
        <ol className="archive-tree-years" aria-label="按年份归档的文章">
          {yearGroups.map(group => (
            <li className="archive-tree-year" key={group.year}>
              <div className="archive-tree-year-heading">
                <h3>{group.year}<span className="archive-tree-year-unit">年</span></h3>
                <span className="archive-tree-count">{group.months.reduce((total, month) => total + month.list.length, 0)} 篇</span>
                <button
                  type="button"
                  className="year-toggle archive-tree-toggle"
                  aria-expanded={!collapsedYears.has(group.year)}
                  aria-controls={`archive-year-${group.year}`}
                  aria-label={`${collapsedYears.has(group.year) ? '展开' : '折叠'} ${group.year} 年文章`}
                  onClick={() => toggleYear(group.year)}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
                </button>
              </div>
              <AnimatedDisclosure open={!collapsedYears.has(group.year)} id={`archive-year-${group.year}`} className="archive-tree-content">
                <ol className="archive-tree-months">
                  {group.months.map(m => {
                    const [,mm] = m.key.split('-');
                    return (
                      <li className="archive-tree-month" key={m.key}>
                        <h4 className="archive-tree-month-heading"><span>{mm} 月</span><span className="archive-tree-count">{m.list.length} 篇</span></h4>
                        <ul className="archive-tree-posts">
                          {m.list.map(item => (
                            <li className="archive-tree-post" key={item.id}>
                              <PostLink className="archive-tree-link" postId={item.id}>
                                <span className="archive-tree-post-title">{item.title}</span>
                                <time className="archive-tree-date" dateTime={item._date.toISOString()}>{String(item._date.getDate()).padStart(2, '0')} 日</time>
                              </PostLink>
                            </li>
                          ))}
                        </ul>
                      </li>
                    );
                  })}
                </ol>
              </AnimatedDisclosure>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// 关于页组件
function About({ appLoading }) {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  usePageReady(!loading && !appLoading);

  useEffect(() => {
    let active = true;
    const loadAbout = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await blogAPI.getAbout();
        if (!active) return;
        setAboutData(data);
      } catch (error) {
        if (!active) return;
        console.error('加载关于信息失败:', error);
        setError(getErrorKind(error));
      } finally {
        if (active) setLoading(false);
      }
    };

    loadAbout();
    return () => { active = false; };
  }, [attempt]);

  // 如果还在加载应用数据或者组件自己的数据，显示加载状态
  if (loading || appLoading) return <CloudLoading label="正在展开关于我的故事…" variant="article" />;
  if (error) return <NotFound kind={error} onRetry={() => setAttempt(value => value + 1)} />;

return (
    <div className="container about-container page-animate">
      <div className="about-card">
        <div className="about-header slide-up-animate">
          <span className="section-eyebrow">HELLO, THERE</span><h2>很高兴，在这里遇见你<span className="heading-dot">.</span></h2>
          <p className="subtitle">{aboutData?.title || '一个热爱技术的开发者'}</p>
        </div>
        
        <div className="about-content">
          <div className="about-section about-intro slide-up-animate">
            <h3>自我介绍</h3>
            <div className="about-bio">
              {(aboutData?.bio || '你好！我是一名前端开发工程师，对Web技术充满热情。')
                .split(/\n\s*\n/).map(paragraph => paragraph.trim()).filter(Boolean)
                .map((paragraph, index) => <p key={index}>{paragraph}</p>)}
            </div>
          </div>
          
          {aboutData?.skills && (
            <div className="about-section skills slide-up-animate">
              <h3>技术栈</h3>
              <div className="skill-tags stagger-animate">
                {aboutData.skills.map(skill => (
                  <span className="tag skill-tag" key={skill}>{skill}</span>
                ))}
              </div>
            </div>
          )}
          
          {aboutData?.contact && (
            <div className="about-section contact slide-up-animate">
              <h3>联系我</h3>
              <div className="contact-list stagger-animate">
                <div className="contact-item">
                  <span className="contact-label">邮箱</span>
                  <a href={`mailto:${aboutData.contact.email}`} className="contact-value">{aboutData.contact.email}</a>
                </div>
                <div className="contact-item">
                  <span className="contact-label">Gitee</span>
                  <a href={aboutData.contact.gitee} target="_blank" rel="noopener noreferrer" className="contact-value">{aboutData.contact.gitee}</a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
